import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Whisper, Tooltip } from "rsuite";
import { HiOutlineCalendar, HiOutlinePlus } from "react-icons/hi";
import AppoointmentTabs from "./AppointmentTabs";
import DefaultPatientFilters from "../../ClinicComponent/Filters/DefaultPatientFilters";
import DoctorAppointmentViewFilters from "../../ClinicComponent/Filters/ClinicFilterJSON/DoctorAppointmentsView";
import { FiUser, FiSearch, FiX } from "react-icons/fi";
import { usePostFilteredAppointmentsMutation } from "../../../State/Api/GetallappointmentsApiSlice";

function DoctorAppointments() {
  const navigate = useNavigate();
  const [AppointementDetail, setAppointementDetail] = useState("");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [hasData, setHasData] = useState(true);

  const gridRef = useRef(null);

  // Keep track of last applied filters
  const prevFiltersRef = useRef([]);

  useEffect(() => {
    // Only purge if filters actually changed
    if (
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(selectedFilters)
    ) {
      gridRef?.current?.api?.purgeInfiniteCache();
      prevFiltersRef.current = selectedFilters;
    }
  }, [selectedFilters]);

  const clinicUserId = localStorage.getItem("userClinicID");
  const doctorUserUniqueID = localStorage.getItem("userUniqueID");

  const [
    postFilteredAppointments,
    { data = [], isLoading, isSuccess, isError, isFetching },
  ] = usePostFilteredAppointmentsMutation();

  const getAppointments = async (pageNumber, filters, sortColumn = "", sortOrder = "") => {
    try {
      const result = await postFilteredAppointments({
        pageNumber: pageNumber,
        pageSize: 100,
        userClinicID: clinicUserId,
        doctorUserUniqueID: doctorUserUniqueID,
        filters: filters,
        sortColumn: sortColumn,
        sortOrder: sortOrder
      }).unwrap();
      return result;
    } catch (err) {
      
    }
  };

  useEffect(() => {
    gridRef?.current?.api?.purgeInfiniteCache();
  }, [selectedFilters]);

  const handleSearch = () => {
    let searchValue = AppointementDetail.trim();

    if (!searchValue) {
      // Clear filters if search empty
      setSelectedFilters((prev) => {
        const { patientName, patientIDUniqueID, ...rest } = prev;
        return rest;
      });
      return;
    }

    let newFilter;

    if (/^PLCRXPAT\d+$/i.test(searchValue)) {
      // Already formatted Patient ID
      newFilter = {
        patientIDUniqueID: {
          values: new Set([searchValue]),
          operator: "Contains",
        },
      };
    } else if (/^\d+$/.test(searchValue)) {
      // Only numbers → prepend prefix
      const prefixedValue = `PLCRXPAT${searchValue}`;
      newFilter = {
        patientIDUniqueID: {
          values: new Set([prefixedValue]),
          operator: "Contains",
        },
      };
    } else {
      // Otherwise treat as name
      newFilter = {
        patientName: {
          values: new Set([searchValue]),
          operator: "Contains",
        },
      };
    }

    setSelectedFilters((prev) => {
      const { patientName, patientIDUniqueID, ...rest } = prev;
      return { ...rest, ...newFilter };
    });
  };

  const handleClearSearch = () => {
    setAppointementDetail("");
    setSelectedFilters((prev) => {
      const { patientName, patientIDUniqueID, ...rest } = prev;
      return rest;
    });
     setHasData(true);
  };

  const tooltipStyles = {
    backgroundColor: "#1f2937",
    color: "white",
    fontSize: "12px",
    padding: "6px 8px",
    borderRadius: "4px",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    border: "none",
    maxWidth: "200px",
  };

  return (
    <>
      <div className="flex flex-col gap-3" style={{ height: "100%", width: "100%" }}>
        <div className="row">
          <div
            style={{ borderBottom: "1px dashed gray", paddingBottom: "1rem" }}
            className="col-12"
          >
            <div className="flex flex-wrap items-center justify-between">
              <div>
                <h2 className="h3 fw-bold mb-0" style={{fontSize:'24px'}}>Appointments</h2>
                {/* <p className="text-secondary">
                  View and manage your appointments{" "}
                </p> */}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <label className="relative">
                  <input
                    type="text"
                    maxLength={14}
                    className="pr-20 pl-4 py-2 border border-[#7eb592] rounded-lg w-full text-sm focus:outline-none ring-2 ring-[#7eb592]/20 focus:outline focus:border-[#7eb592] focus:ring-2 focus:ring-[#7eb592]/20"
                    placeholder="Enter patient name or ID"
                    value={AppointementDetail}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAppointementDetail(value);

                      if (value.trim() === "") {
                        setSelectedFilters((prev) => {
                          const { fullName, patientIDUniqueID, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />

                  {AppointementDetail && (
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={
                        <Tooltip className="!bg-gray-800 !text-white !text-xs !px-2 !py-1 !rounded !shadow-lg">
                          Clear search
                        </Tooltip>
                      }
                    >
                      <span
                        className="absolute right-12 top-9 transform -translate-y-1/2 p-2 rounded-md text-gray-500 hover:text-red-400 cursor-pointer transition-colors duration-200"
                        onClick={handleClearSearch}
                      >
                        <FiX size={16} />
                      </span>
                    </Whisper>
                  )}

                  <Whisper
                    placement="bottom"
                    trigger="hover"
                    delay={100}
                    speaker={
                      <Tooltip style={tooltipStyles}>
                        Search patients by name or Only last 4 digits of ID or
                        Department
                      </Tooltip>
                    }
                  >
                    <span
                      className="absolute right-[0.3rem] top-9 transform -translate-y-1/2 bg-[#7eb592] hover:bg-[#6ba080] p-2 rounded-md text-white cursor-pointer transition-colors duration-200"
                      onClick={handleSearch}
                    >
                      <FiSearch size={16} />
                    </span>
                  </Whisper>
                </label>

                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip>Filter Options</Tooltip>}
                >
                  <DefaultPatientFilters
                    defaultFilters={DoctorAppointmentViewFilters.default}
                    additionalFilters={DoctorAppointmentViewFilters.additional}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    setHasData={setHasData}
                  />
                </Whisper>
              </div>
            </div>
          </div>
        </div>

        <div className="" style={{ height: "100%", width: "100%" }}>
          <AppoointmentTabs 
            getAppointments={getAppointments}
                isFetching={isFetching}
                isLoading={isLoading}
                isSuccess={isSuccess}
                gridRef={gridRef}
                selectedFilters={selectedFilters}
                hasData={hasData}          
                setHasData={setHasData} 
          />
        </div>
      </div>
    </>
  );
}

export default DoctorAppointments;
